import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import OrdersHistory from "./components/OrdersHistory";
import TransactionsHistory from "./components/TransactionsHistory";
import useCustomerDetail from "./../../features/customers/hooks/useCustomerDetail";

function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    customer,
    orders,
    transactions,
    loading,
    error,
    resetAlerts,
  } = useCustomerDetail(id);

  const [tab, setTab] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [addAmount, setAddAmount] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const balance = customer?.wallet?.balance ?? customer?.wallet_balance ?? 0;
    setWalletBalance(Number(balance) || 0);
  }, [customer]);

  useEffect(() => {
    setWalletTransactions(transactions || []);
  }, [transactions]);

  const walletSummary = useMemo(() => {
    const wallet = customer?.wallet || {};
    const resolveEntry = (entry) => {
      if (!entry) return null;
      if (typeof entry === "number") {
        return { amount: entry, date: null };
      }
      return {
        amount: Number(entry.amount ?? entry.value ?? 0),
        date: entry.date || entry.created_at || entry.createdAt || null,
      };
    };

    return {
      balance: walletBalance,
      last_recharge: resolveEntry(wallet.last_recharge),
      last_spend: resolveEntry(wallet.last_spend),
    };
  }, [customer, walletBalance]);

  const recentOrders = useMemo(() => orders || [], [orders]);

  const handleTabChange = (_event, value) => setTab(value);

  const handleAddConfirm = () => {
    const value = parseFloat(addAmount);
    if (Number.isNaN(value) || value <= 0) {
      return;
    }

    setWalletBalance((prev) => prev + value);
    setWalletTransactions((prev) => [
      {
        id: Date.now(),
        date: new Date().toISOString(),
        type: "credit",
        amount: value,
        desc: "Admin Top-up",
      },
      ...prev,
    ]);
    setSuccess("Amount added locally to wallet.");
    setAddOpen(false);
    setAddAmount("");
  };

  const handleCloseDialog = () => {
    setAddOpen(false);
    setAddAmount("");
  };

  const handleSnackbarClose = () => {
    resetAlerts();
    setSuccess(null);
  };

  const displayCustomer = customer || {};
  const joinedAt = displayCustomer.joinedAt || displayCustomer.createdAt || displayCustomer.created_at;
  const totalOrders = displayCustomer.total_orders ?? displayCustomer.orders_count ?? recentOrders.length ?? 0;

  const formatEntry = (entry) => {
    if (!entry) return "No data";
    const amount = Number(entry.amount ?? 0).toFixed(2);
    const date = entry.date ? new Date(entry.date).toLocaleString() : "";
    return `$${amount}${date ? ` on ${date}` : ""}`;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3} px={2}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <MDTypography variant="h5">Customer Detail</MDTypography>
          <Button variant="outlined" style={{ color: "black" }} onClick={() => navigate(-1)}>
            Back
          </Button>
        </MDBox>

        {!displayCustomer?.id && !loading ? (
          <Card>
            <CardContent>
              <MDTypography variant="body1">Customer information could not be found.</MDTypography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader title="Profile Information" />
                <CardContent>
                  <MDBox lineHeight={1.6}>
                    <MDTypography variant="button"><strong>Name:</strong> {displayCustomer.full_name || "-"}</MDTypography><br />
                    <MDTypography variant="button"><strong>Email:</strong> {displayCustomer.email || "-"}</MDTypography><br />
                    <MDTypography variant="button"><strong>Phone:</strong> {displayCustomer.contact || displayCustomer.phone || "-"}</MDTypography><br />
                    <MDTypography variant="button"><strong>Address:</strong> {displayCustomer.address || "-"}</MDTypography><br />
                    <MDTypography variant="button"><strong>Status:</strong> {displayCustomer.status || "-"}</MDTypography><br />
                    <MDTypography variant="button"><strong>Joined:</strong> {joinedAt ? new Date(joinedAt).toLocaleString() : "-"}</MDTypography><br />
                    <MDTypography variant="button"><strong>Total Orders:</strong> {totalOrders}</MDTypography>
                  </MDBox>
                </CardContent>
              </Card>

              <MDBox mt={2} />
              <Card>
                <CardHeader
                  title="Wallet Summary"
                  action={
                    <Button variant="contained" size="small" style={{ color: "white" }} onClick={() => setAddOpen(true)}>
                      Add Amount
                    </Button>
                  }
                />
                <CardContent>
                  <MDTypography variant="button"><strong>Balance:</strong> ${Number(walletSummary.balance || 0).toFixed(2)}</MDTypography><br />
                  <MDTypography variant="button"><strong>Last Recharge:</strong> {formatEntry(walletSummary.last_recharge)}</MDTypography><br />
                  <MDTypography variant="button"><strong>Last Spend:</strong> {formatEntry(walletSummary.last_spend)}</MDTypography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card>
                <CardHeader title="Activity" />
                <Tabs value={tab} onChange={handleTabChange} sx={{ px: 2 }}>
                  <Tab label="Orders" />
                  <Tab label="Wallet Transactions" />
                </Tabs>
                <MDBox p={2}>
                  {tab === 0 ? (
                    <OrdersHistory currentId={id} title="Recent Orders" />
                  ) : (
                    <TransactionsHistory transactions={walletTransactions} title="Wallet Transactions" />
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
      <Footer />

      <Dialog open={addOpen} onClose={handleCloseDialog}>
        <DialogTitle>Add Amount to Wallet</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            value={addAmount}
            onChange={(event) => setAddAmount(event.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" style={{ color: "white" }} onClick={handleAddConfirm}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={error ? "error" : "success"} sx={{ width: "100%" }}>
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default CustomerDetail;
